import { PlotData } from 'plotly.js'

/** Convert hex / rgb(a) theme colors to rgba with a given alpha (hex#aa suffixes break on rgb()). */
export const withAlpha = (color: string, alpha: number): string => {
  const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
  if (rgba) {
    return `rgba(${rgba[1]}, ${rgba[2]}, ${rgba[3]}, ${alpha})`
  }
  const hex = color.match(/^#([0-9a-f]{6})$/i)
  if (hex) {
    const n = parseInt(hex[1], 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
  }
  return color
}

const bandLabel = (band: number) =>
  Number.isInteger(band) ? String(band) : band.toFixed(2)

/** ±band envelope around y=0 (signed / scaled residual charts), tinted by model color. */
export const residualBandTraces = (
  dates: string[],
  band: number,
  color: string,
  name: string
): Partial<PlotData>[] => {
  if (!Number.isFinite(band) || dates.length === 0) return []
  const label = bandLabel(band)
  const edge = withAlpha(color, 0.95)
  const fill = withAlpha(color, 0.18)
  return [
    {
      x: dates,
      y: dates.map(() => band),
      type: 'scatter',
      mode: 'lines',
      name: `${name} ±${label}`,
      legendgroup: `band-${name}`,
      line: { width: 3, dash: 'dash', color: edge },
      showlegend: true,
      hovertemplate: `${name} +band: %{y}<extra></extra>`,
    },
    {
      x: dates,
      y: dates.map(() => -band),
      type: 'scatter',
      mode: 'lines',
      name: `${name} −band`,
      legendgroup: `band-${name}`,
      line: { width: 3, dash: 'dash', color: edge },
      fill: 'tonexty',
      fillcolor: fill,
      showlegend: false,
      hovertemplate: `${name} −band: %{y}<extra></extra>`,
    },
  ]
}

/** ±residual_band envelope around an expected series (actual vs expected chart). */
export const expectedBandTraces = (
  dates: string[],
  expected: (number | null)[],
  band: number,
  color: string,
  name: string
): Partial<PlotData>[] => {
  if (!Number.isFinite(band) || dates.length === 0) return []
  const label = bandLabel(band)
  const edge = withAlpha(color, 0.9)
  const fill = withAlpha(color, 0.14)
  const upper = expected.map((v) =>
    v === null || v === undefined ? null : v + band
  )
  const lower = expected.map((v) =>
    v === null || v === undefined ? null : v - band
  )
  return [
    {
      x: dates,
      y: upper,
      type: 'scatter',
      mode: 'lines',
      name: `${name} ±${label}`,
      legendgroup: `exp-band-${name}`,
      line: { width: 2.5, dash: 'dash', color: edge },
      showlegend: true,
      hovertemplate: `${name} +band: %{y}<extra></extra>`,
    },
    {
      x: dates,
      y: lower,
      type: 'scatter',
      mode: 'lines',
      name: `${name} −band`,
      legendgroup: `exp-band-${name}`,
      line: { width: 2.5, dash: 'dash', color: edge },
      fill: 'tonexty',
      fillcolor: fill,
      showlegend: false,
      hovertemplate: `${name} −band: %{y}<extra></extra>`,
    },
  ]
}
